import type { ReactNode } from "react"
import { createContext, useContext } from "react"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import type { Typography } from "@onestore/hel/dist/contexts/TypographyContext"
import type Theme from "@onestore/hel/dist/typings/theme"
import ColorTagElement from "@gatsby-plugin-generic-page/components/Markdown/ColorTagElement"
import type { NestedLinkProps } from "@gatsby-plugin-generic-page/components/NestedLink"
import NestedLink from "@gatsby-plugin-generic-page/components/NestedLink"
import type { ArrowLinkProps } from "@gatsby-plugin-generic-page/markdown/components/ArrowLink"
import ArrowLink from "@gatsby-plugin-generic-page/markdown/components/ArrowLink"
import type { LinkProps as MarkdownLinkProps } from "@gatsby-plugin-generic-page/markdown/components/Link"
import MarkdownLink from "@gatsby-plugin-generic-page/markdown/components/Link"
import { BrandCheckmark, Link, Text } from "@ionos-web-design-system/react"
import type { Components } from "react-markdown"
import {
  helEmphasisToUdsColor,
  helTypographyToUdsProps,
  type UdsColor,
} from "./helTypographyToUds"

const ListTypeContext = createContext<"ul" | "ol" | null>(null)

interface BaseMarkdownOverridesOptions {
  typography: Typography
  boldTypography?: Typography
  emphasis?: Theme.Emphasis
  /** @deprecated no longer used – lists are rendered natively */
  listSize?: string
  pushSpace?: number
  alignment?: "left" | "center" | "right"
  color?: UdsColor
}

function getBaseMarkdownOverrides({
  typography,
  boldTypography,
  emphasis,
  pushSpace,
  alignment,
  color: colorProp,
}: BaseMarkdownOverridesOptions): Components {
  const udsProps = helTypographyToUdsProps(typography as Theme.Typography)
  const boldUdsProps = boldTypography
    ? helTypographyToUdsProps(boldTypography as Theme.Typography)
    : { ...udsProps, weight: "bold" as const }
  const color = colorProp ?? helEmphasisToUdsColor(emphasis)
  const marginStyle = pushSpace
    ? { marginBottom: `${pushSpace * 0.5}rem` }
    : undefined

  const strongHTMLTag = boldTypography && {
    strong: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...boldUdsProps}
        color={color || "base"}
        style={marginStyle}
        alignment={alignment}
        weight="black"
      >
        <strong>{children}</strong>
      </Text>
    ),
  }

  return {
    ...strongHTMLTag,
    h1: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...udsProps}
        color={color || "base"}
        style={marginStyle}
        weight="black"
        alignment={alignment}
        className="leading-normal"
      >
        <h1>{children}</h1>
      </Text>
    ),
    h2: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...udsProps}
        color={color || "base"}
        style={marginStyle}
        alignment={alignment}
        className="leading-normal"
      >
        <h2>{children}</h2>
      </Text>
    ),
    h3: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...udsProps}
        color={color || "base"}
        style={marginStyle}
        alignment={alignment}
        className="leading-normal"
      >
        <h3>{children}</h3>
      </Text>
    ),
    p: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...udsProps}
        color={color || "base"}
        style={marginStyle}
        alignment={alignment}
        className="leading-normal"
      >
        <p>{children}</p>
      </Text>
    ),
    span: ({ children }: { children: ReactNode }) => (
      <Text
        asChild
        {...udsProps}
        color={color || "base"}
        style={marginStyle}
        alignment={alignment}
        className="leading-normal"
      >
        <span>{children}</span>
      </Text>
    ),
    a: ({
      href,
      children,
      title,
    }: {
      href?: string
      children: ReactNode
      title?: string
    }) => {
      const isEmailHref =
        !!href &&
        (href.startsWith("mailto:") || /^[^\s/]+@[^\s/]+\.[^\s/]+$/.test(href))

      if (isEmailHref) {
        return <>{children}</>
      }

      if (!href) {
        return (
          <Text
            asChild
            {...udsProps}
            color={color || "base"}
            style={{ textAlign: "inherit" }}
          >
            <span>{children}</span>
          </Text>
        )
      }

      const isInternal = /^\.{0,2}\//.test(href)

      return (
        <Text asChild {...udsProps} color={color || "base"}>
          <Link
            concept="mono"
            href={href}
            title={title || ""}
            {...(!isInternal && {
              target: "_blank",
              rel: "noopener noreferrer",
            })}
          >
            {children}
          </Link>
        </Text>
      )
    },
    mdlink: ({
      node: _node,
      typography: _typography,
      ...props
    }: MarkdownLinkProps & { node?: unknown }) => (
      <MarkdownLink typography={typography as Theme.Typography} {...props} />
    ),
    nestedlink: ({
      node: _node,
      to,
      href,
      title,
      children,
      typography: _typography,
      ...restProps
    }: NestedLinkProps & { node?: unknown }) => (
      <NestedLink
        typography={typography as Theme.Typography}
        to={to}
        href={href}
        title={title || ""}
        {...restProps}
      >
        {children}
      </NestedLink>
    ),
    arrowlink: ({
      node: _node,
      to,
      href,
      title,
      children,
      ...restProps
    }: ArrowLinkProps & { node?: unknown }) => (
      <ArrowLink to={to} href={href} title={title} {...restProps}>
        {children}
      </ArrowLink>
    ),
    color: ({
      node: _node,
      value,
      children,
    }: {
      node?: unknown
      value?: Theme.AccentColor
      children: ReactNode
    }) => (
      <ColorTagElement
        typography={typography as Theme.Typography}
        value={value as Theme.AccentColor}
      >
        {children}
      </ColorTagElement>
    ),
    ul: ({ children }: { children: ReactNode }) => (
      <ListTypeContext.Provider value="ul">
        <PushOrg bottomSpace={2}>
          <ul className="list-none p-0 m-0">{children}</ul>
        </PushOrg>
      </ListTypeContext.Provider>
    ),
    ol: ({ children }: { children: ReactNode }) => (
      <ListTypeContext.Provider value="ol">
        <PushOrg bottomSpace={2}>
          <ol>{children}</ol>
        </PushOrg>
      </ListTypeContext.Provider>
    ),
    li: ({ children }: { children: ReactNode }) => {
      const listType = useContext(ListTypeContext)

      if (listType === "ul") {
        return (
          <li className="flex gap-3 mb-2 items-start font-light leading-normal text-base">
            <BrandCheckmark />
            <div className="flex-1">{children}</div>
          </li>
        )
      }

      return (
        <Text asChild {...udsProps} color={color || "base"}>
          <li>{children}</li>
        </Text>
      )
    },
  } as Components
}

export default getBaseMarkdownOverrides
